import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Theater, MessageSquare, Loader2, Calendar, MapPin, Ban, CheckCircle2,
} from "lucide-react";
import TheaterBookingThreadDrawer from "@/components/TheaterBookingThreadDrawer";

const STATUS_TABS = [
  { key: "all",             label: "All" },
  { key: "requested",       label: "Awaiting" },
  { key: "counter_offered", label: "Countered" },
  { key: "accepted",        label: "Accepted" },
  { key: "confirmed",       label: "Confirmed" },
  { key: "declined",        label: "Declined" },
  { key: "cancelled",       label: "Cancelled" },
];

const STATUS_TONE = {
  requested:       "bg-amber-500/15 text-amber-300",
  counter_offered: "bg-sky-500/15 text-sky-300",
  accepted:        "bg-emerald-500/15 text-emerald-300",
  confirmed:       "bg-emerald-500/25 text-emerald-200",
  declined:        "bg-red-500/15 text-red-300",
  cancelled:       "bg-white/10 text-white/50",
};

/**
 * Theater Group's bookings inbox. Read-only for the group (except cancel).
 * Every row opens a shared thread drawer.
 */
export default function TheaterGroupBookings() {
  const [tab, setTab] = useState("all");
  const [data, setData] = useState({ bookings: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null);

  const load = () => {
    setLoading(true);
    const params = tab === "all" ? {} : { status: tab };
    api.get("/theater-group/bookings/mine", { params })
      .then(({ data }) => setData(data || { bookings: [], counts: {} }))
      .catch(() => setData({ bookings: [], counts: {} }))
      .finally(() => setLoading(false));
  };

  useEffect(load, [tab]);

  const cancel = async (id) => {
    if (!confirm("Cancel this booking request?")) return;
    try {
      await api.patch(`/theater-bookings/${id}/cancel`);
      toast.success("Booking cancelled.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not cancel.");
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10"
         data-testid="theater-group-bookings">
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">L17 · My Bookings</div>
      <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-tight mt-2 text-white">
        Venue Bookings
      </h1>

      {/* TABS */}
      <div className="mt-8 flex flex-wrap gap-2" data-testid="theater-group-bookings-tabs">
        {STATUS_TABS.map((t) => {
          const count = t.key === "all"
            ? Object.values(data.counts || {}).reduce((a, b) => a + b, 0)
            : (data.counts?.[t.key] || 0);
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`h-9 px-3 rounded-full text-[10px] tracking-[0.22em] uppercase font-semibold transition ${
                active ? "bg-orange-500 text-black" : "bg-white/8 text-white/60 hover:bg-white/15"
              }`}
              data-testid={`theater-group-bookings-tab-${t.key}`}>
              {t.label}{count > 0 && ` · ${count}`}
            </button>
          );
        })}
      </div>

      {/* LIST */}
      <div className="mt-6">
        {loading ? (
          <div className="cs-card rounded-xl p-8 text-center text-white/50">
            <Loader2 size={14} className="animate-spin inline mr-2"/> loading…
          </div>
        ) : data.bookings.length === 0 ? (
          <div className="cs-card rounded-xl p-10 text-center" data-testid="theater-group-bookings-empty">
            <Calendar size={26} className="mx-auto text-white/25 mb-4"/>
            <div className="text-white/70 text-sm">No bookings in this status.</div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="theater-group-bookings-list">
            {data.bookings.map((b) => (
              <BookingRow key={b.id} b={b}
                          onCancel={() => cancel(b.id)}
                          onOpenThread={() => setDrawer(b)}/>
            ))}
          </div>
        )}
      </div>

      <TheaterBookingThreadDrawer
        booking={drawer}
        myRole="group"
        open={!!drawer}
        onClose={() => { setDrawer(null); load(); }}
        onSent={load}
      />
    </div>
  );
}

function BookingRow({ b, onCancel, onOpenThread }) {
  const canCancel = b.status === "requested" || b.status === "counter_offered";
  return (
    <div className="cs-card rounded-xl p-5 flex flex-wrap items-start justify-between gap-3"
         data-testid={`theater-group-booking-${b.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-display text-base uppercase tracking-tight text-white">{b.hall_name || "Hall"}</span>
          <span className={`px-2 py-0.5 rounded-full text-[9px] tracking-[0.18em] uppercase ${STATUS_TONE[b.status] || "bg-white/8 text-white/60"}`}
                data-testid={`theater-group-booking-status-${b.id}`}>
            {b.status.replace("_", " ")}
          </span>
        </div>
        <div className="mt-1 text-[11px] text-white/55">
          <MapPin size={9} className="inline mr-1"/> {b.hall_city}
          {b.production_title && <span> · {b.production_title}</span>}
        </div>
        <div className="mt-3 flex flex-wrap gap-4 text-[11px] text-white/70">
          <div>
            <div className="text-[9px] uppercase tracking-[0.22em] text-white/40">Date · Slot</div>
            <div className="mt-0.5">{b.requested_date} · {b.slot}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-[0.22em] text-white/40">Your Offer</div>
            <div className="mt-0.5">₹{(b.offered_price || 0).toLocaleString()}</div>
          </div>
          {b.status === "counter_offered" && (
            <div>
              <div className="text-[9px] uppercase tracking-[0.22em] text-sky-300">Hall Counter</div>
              <div className="mt-0.5 text-sky-200">₹{(b.counter_price || 0).toLocaleString()}{b.counter_date && ` on ${b.counter_date}`}</div>
            </div>
          )}
        </div>
        {b.note && (
          <p className="mt-3 text-[11px] text-white/55 leading-relaxed line-clamp-2">"{b.note}"</p>
        )}
        {b.last_status_note && (
          <p className="mt-2 text-[11px] text-sky-200/70 leading-relaxed">Hall note: {b.last_status_note}</p>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button onClick={onOpenThread}
                className="h-9 rounded-full text-[10px] uppercase tracking-[0.22em] bg-white/8 hover:bg-white/15 text-white"
                data-testid={`theater-group-booking-thread-${b.id}`}>
          <MessageSquare size={12} className="mr-1.5"/> Thread
        </Button>
        {canCancel && (
          <Button onClick={onCancel}
                  className="h-9 rounded-full text-[10px] uppercase tracking-[0.22em] bg-red-500/20 hover:bg-red-500/30 text-red-300"
                  data-testid={`theater-group-booking-cancel-${b.id}`}>
            <Ban size={12} className="mr-1.5"/> Cancel
          </Button>
        )}
      </div>
    </div>
  );
}
